import { Box } from '@mui/material';

import { useAxisHoverMark } from './axisHover';

interface AxisGridlineHoverTargetProps {
  /** The anchor column this strip sits in, i.e. the boundary its own gridline draws. */
  field: string;
}

/**
 * How wide a pointer target the gridline gets. The line is 1px (2px hovered), which no one can
 * aim at, so the strip straddles it symmetrically — four pixels of slack either side, the same
 * order of forgiveness a resize handle gets.
 */
const HOVER_TARGET_WIDTH = 9;
const GRIDLINE_WIDTH = 1;

// The gridline is the anchor cell's own ::before at `left: 0`, so the strip that straddles it
// starts half its slack to the LEFT of the cell — which the period cell's `overflow: visible`
// already allows. Transparent, and never painted: it exists only to be hovered.
const HOVER_TARGET_SX = {
  position: 'absolute' as const,
  top: 0,
  bottom: 0,
  left: `${-(HOVER_TARGET_WIDTH - GRIDLINE_WIDTH) / 2}px`,
  width: `${HOVER_TARGET_WIDTH}px`,
  cursor: 'default',
};

/**
 * The gridline's own end of the boundary hover.
 *
 * A pseudo-element has no `:hover` of its own and cannot be aimed at anyway, so each anchor cell
 * carries this transparent strip over its line, raising the same root mark the header date raises.
 * Hovering either end therefore lights both: the line thickens into the accent, and the date it
 * points at takes the accent too.
 *
 * Rendered FIRST in the cell so that everything drawn after it wins the hit test where they
 * overlap — a booking bar keeps its own tooltip and click, and the strip answers in the full-height
 * space above and below the bar lane.
 */
const AxisGridlineHoverTarget = ({ field }: AxisGridlineHoverTargetProps) => {
  const { markHover, clearHoverMark, hoverRef } = useAxisHoverMark();

  return (
    <Box
      ref={hoverRef}
      data-testid="axis-gridline-hover-target"
      onMouseEnter={markHover(field)}
      onMouseLeave={clearHoverMark}
      sx={HOVER_TARGET_SX}
    />
  );
};

export default AxisGridlineHoverTarget;
